/**
 * SyproFlow AI Chat Widget
 * ========================
 * Floating chat bubble for app.syproflow.com
 * 
 * Usage: Import and add <ChatWidget /> at the end of your App component.
 * 
 * Version: 1.0.0
 * Date: February 20, 2026
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';

const getApiBase = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'app.syproflow.com' || host === 'syproflow.com' || host === 'www.syproflow.com') {
      return 'https://api.syproflow.com';
    }
  }
  return import.meta?.env?.VITE_API_BASE_URL || 'http://localhost:8000';
};

const API = getApiBase();

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! I\'m SyproFlow AI. Ask me anything about document extraction, exports, or Tally integration.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const history = [...messages, userMsg].slice(-10).map(m => ({
        role: m.role, content: m.content
      }));

      const res = await fetch(`${API}/api/v1/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, history })
      });

      if (!res.ok) throw new Error('Chat failed');
      const data = await res.json();

      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }]);
      if (!isOpen) setUnread(prev => prev + 1);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I\'m having trouble connecting. Please try again or reach us on WhatsApp at +91 7304874746.'
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, isOpen]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Quick suggestion chips
  const suggestions = [
    'What documents do you support?',
    'How does Tally export work?',
    'What are the export formats?',
  ];

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div style={{
          position: 'fixed', bottom: '88px', right: '20px', zIndex: 10000,
          width: '380px', maxWidth: 'calc(100vw - 40px)', height: '520px', maxHeight: 'calc(100vh - 120px)',
          borderRadius: '16px', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
          display: 'flex', flexDirection: 'column', background: '#fff',
          animation: 'chatSlideUp 0.25s ease-out'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #ea580c, #f97316)',
            padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px'
          }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: '18px'
            }}>🤖</div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#fff', fontWeight: 600, fontSize: '15px' }}>SyproFlow AI</div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '11px' }}>
                {loading ? 'Typing...' : 'Ask me anything'}
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', cursor: 'pointer', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px'
            }}>✕</button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '16px',
            display: 'flex', flexDirection: 'column', gap: '12px',
            background: '#f8fafc'
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
              }}>
                <div style={{
                  maxWidth: '85%', padding: '10px 14px', borderRadius: '14px',
                  fontSize: '13.5px', lineHeight: '1.5',
                  ...(msg.role === 'user' ? {
                    background: '#ea580c', color: '#fff',
                    borderBottomRightRadius: '4px'
                  } : {
                    background: '#fff', color: '#1e293b',
                    border: '1px solid #e2e8f0',
                    borderBottomLeftRadius: '4px'
                  })
                }}>
                  {msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  background: '#fff', border: '1px solid #e2e8f0',
                  padding: '10px 18px', borderRadius: '14px', borderBottomLeftRadius: '4px',
                  display: 'flex', gap: '4px'
                }}>
                  <span style={{ animation: 'dotPulse 1.4s infinite', animationDelay: '0s' }}>●</span>
                  <span style={{ animation: 'dotPulse 1.4s infinite', animationDelay: '0.2s' }}>●</span>
                  <span style={{ animation: 'dotPulse 1.4s infinite', animationDelay: '0.4s' }}>●</span>
                </div>
              </div>
            )}

            {/* Quick suggestions — show only if conversation has just the welcome message */}
            {messages.length === 1 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => { setInput(s); setTimeout(sendMessage, 50); }}
                    style={{
                      background: '#fff', border: '1px solid #e2e8f0', borderRadius: '20px',
                      padding: '6px 12px', fontSize: '12px', color: '#ea580c', cursor: 'pointer',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => { e.target.style.background = '#fff7ed'; e.target.style.borderColor = '#ea580c'; }}
                    onMouseLeave={e => { e.target.style.background = '#fff'; e.target.style.borderColor = '#e2e8f0'; }}
                  >{s}</button>
                ))}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '12px 16px', borderTop: '1px solid #e2e8f0',
            display: 'flex', gap: '8px', background: '#fff'
          }}>
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask about SyproFlow..."
              style={{
                flex: 1, border: '1px solid #e2e8f0', borderRadius: '24px',
                padding: '10px 16px', fontSize: '13.5px', outline: 'none',
                transition: 'border-color 0.15s'
              }}
              onFocus={e => e.target.style.borderColor = '#ea580c'}
              onBlur={e => e.target.style.borderColor = '#e2e8f0'}
            />
            <button onClick={sendMessage} disabled={!input.trim() || loading}
              style={{
                width: '40px', height: '40px', borderRadius: '50%',
                border: 'none', cursor: input.trim() && !loading ? 'pointer' : 'default',
                background: input.trim() && !loading ? '#ea580c' : '#cbd5e1',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '16px', transition: 'background 0.15s'
              }}
            >➤</button>
          </div>
        </div>
      )}

      {/* Floating Bubble */}
      <button onClick={() => setIsOpen(!isOpen)} style={{
        position: 'fixed', bottom: '24px', right: '24px', zIndex: 10000,
        width: '56px', height: '56px', borderRadius: '50%',
        background: 'linear-gradient(135deg, #ea580c, #f97316)',
        border: 'none', cursor: 'pointer', color: '#fff',
        boxShadow: '0 6px 24px rgba(234,88,12,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '24px', transition: 'transform 0.2s, box-shadow 0.2s'
      }}
        onMouseEnter={e => { e.target.style.transform = 'scale(1.08)'; e.target.style.boxShadow = '0 8px 32px rgba(234,88,12,0.5)'; }}
        onMouseLeave={e => { e.target.style.transform = 'scale(1)'; e.target.style.boxShadow = '0 6px 24px rgba(234,88,12,0.4)'; }}
      >
        {isOpen ? '✕' : '💬'}
        {unread > 0 && !isOpen && (
          <span style={{
            position: 'absolute', top: '-4px', right: '-4px',
            background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 700,
            width: '20px', height: '20px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #fff'
          }}>{unread}</span>
        )}
      </button>

      {/* Animations */}
      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes dotPulse {
          0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
          30% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </>
  );
}